import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBolt, FaMapMarkerAlt, FaQrcode, FaUtensils } from 'react-icons/fa';
import { restaurantAPI } from '../utils/api';
import { useRestaurantId } from '../utils/restaurantContext';
import './RestaurantLandingPage.css';

export default function RestaurantLandingPage() {
  const navigate = useNavigate();
  const restaurantId = useRestaurantId();

  const [restaurantName, setRestaurantName] = useState('Restaurant');
  const [loading, setLoading] = useState(true);
  // Auto-typing header
  const typingTextFull = 'WELCOME IN MenuBazzar';
  const [typingIndex, setTypingIndex] = useState(0);
  const [typingForward, setTypingForward] = useState(true);

  const safeRestaurantId = useMemo(() => {
    return restaurantId ? String(restaurantId) : null;
  }, [restaurantId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!safeRestaurantId) {
        setLoading(false);
        return;
      }

      try {
        const response = await restaurantAPI.getById(safeRestaurantId);
        const name = response?.data?.restaurant?.name;
        if (!cancelled) {
          setRestaurantName(name || 'Restaurant');
        }
      } catch (e) {
        // Keep generic fallback if API fails.
        if (!cancelled) {
          setRestaurantName('Restaurant');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [safeRestaurantId]);

  useEffect(() => {
    const min = 0;
    const max = typingTextFull.length;

    const interval = setInterval(() => {
      setTypingIndex((current) => {
        // Drive direction changes by current state.
        if (typingForward && current >= max) {
          setTypingForward(false);
          return max;
        }
        if (!typingForward && current <= min) {
          setTypingForward(true);
          return min;
        }
        return typingForward ? current + 1 : current - 1;
      });
    }, 90);

    return () => clearInterval(interval);
  }, [typingForward, typingTextFull.length]);

  const handleViewMenu = () => {
    if (!safeRestaurantId) {
      navigate('/customer/menu');
      return;
    }

    // Ask for location access (browser prompt) before moving to menu.
    // We proceed to the menu after the prompt regardless of allow/deny.
    if (!('geolocation' in navigator)) {
      navigate('menu');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        navigate('menu');
      },
      () => {
        navigate('menu');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  return (
    <div className="restaurant-landing">
      <div className="restaurant-landing-bg" aria-hidden="true">
        <div className="restaurant-landing-pattern" />
        <div className="restaurant-landing-glow restaurant-landing-glow--one" />
        <div className="restaurant-landing-glow restaurant-landing-glow--two" />
      </div>

      <main className="restaurant-landing-shell">
        <section className="restaurant-landing-card" aria-label="Restaurant welcome">
          <div className="rl-topline">
            <span className="rl-brandDot" aria-hidden="true" />
            <span className="rl-typing" aria-label="Welcome in MenuBazzar">
              {typingTextFull.slice(0, typingIndex)}
              <span className="rl-caret" aria-hidden="true" />
            </span>
          </div>

          <h1 className={`restaurant-landing-title ${loading ? 'is-loading' : ''}`}>
            {loading ? <span className="rl-skeleton rl-skeleton--title" /> : restaurantName}
          </h1>

          {loading ? (
            <div className="rl-skeletonStack" aria-hidden="true">
              <div className="rl-skeleton rl-skeleton--line" />
              <div className="rl-skeleton rl-skeleton--line rl-skeleton--lineShort" />
            </div>
          ) : (
            <p className="restaurant-landing-subtitle">
              Your smart dining companion. Scan the QR, explore dishes, and place your order in seconds.
            </p>
          )}

          <div className="rl-featureGrid" aria-label="Highlights">
            <div className="rl-feature">
              <div className="rl-featureIcon" aria-hidden="true">
                <FaQrcode />
              </div>
              <div className="rl-featureText">
                <div className="rl-featureTitle">QR Ready</div>
                <div className="rl-featureDesc">Instant access to the menu</div>
              </div>
            </div>

            <div className="rl-feature">
              <div className="rl-featureIcon" aria-hidden="true">
                <FaUtensils />
              </div>
              <div className="rl-featureText">
                <div className="rl-featureTitle">Explore Dishes</div>
                <div className="rl-featureDesc">Clear categories & details</div>
              </div>
            </div>

            <div className="rl-feature">
              <div className="rl-featureIcon" aria-hidden="true">
                <FaBolt />
              </div>
              <div className="rl-featureText">
                <div className="rl-featureTitle">Fast Ordering</div>
                <div className="rl-featureDesc">Add to cart in one tap</div>
              </div>
            </div>

            <div className="rl-feature">
              <div className="rl-featureIcon" aria-hidden="true">
                <FaMapMarkerAlt />
              </div>
              <div className="rl-featureText">
                <div className="rl-featureTitle">Location Prompt</div>
                <div className="rl-featureDesc">Helps personalize your visit</div>
              </div>
            </div>
          </div>

          <div className="restaurant-landing-actions">
            <button
              className={`restaurant-landing-cta ${loading ? 'is-loading' : ''}`}
              type="button"
              onClick={handleViewMenu}
              disabled={!safeRestaurantId || loading}
            >
              {loading ? <span className="rl-skeleton rl-skeleton--button" /> : 'View Menu'}
            </button>

            <p className="restaurant-landing-permissionHint">
              We’ll ask for your location once before showing the menu.
            </p>
          </div>

          {!safeRestaurantId && (
            <p className="restaurant-landing-footnote">
              Missing restaurant ID. Please scan the QR code again.
            </p>
          )}
        </section>
      </main>
    </div>
  );
}
